/**
 * Is messaging working, per provider — the page to open when a client says
 * "my messages aren't going".
 *
 * Every figure is the server's, read from what is already recorded: connection
 * states, the last day's sends and their delivery, reports still waiting, sends
 * the worker has not finished. Nothing here calls a gateway; a provider that is
 * down shows up as failing connections and rising failures, which is how it
 * shows up for clients too.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  ApiError,
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatCard,
  StatRow,
  api,
  formatNumber,
  formatRelative,
} from '@connect/web-shared';

import { ChannelIcon } from './channel-icon';
import { SendReconciliationCard } from './send-reconciliation-card';
import { SignInWhatsAppCard } from './sign-in-whatsapp-card';

interface FailingConnection {
  client_public_id: string;
  client_name: string;
  channel_public_id: string;
  label: string;
  last_error: string;
}

interface ProviderHealth {
  id: string;
  label: string;
  channel: string;
  connections: {
    total: number;
    active: number;
    paused: number;
    verified: number;
    failing: number;
    low_balance: number;
  };
  sends_24h: number;
  messages_24h: number;
  delivered_24h: number;
  failed_24h: number;
  /** Failed as a share of messages with a final answer, e.g. `"3.2"`. */
  failure_rate: string | null;
  /** Accepted by the gateway over half an hour ago and still without a report. */
  waiting_reports: number;
  failing: FailingConnection[];
}

interface MessagingHealth {
  providers: ProviderHealth[];
  worker: {
    enabled: boolean;
    queued_sends: number;
    stalled_sends: number;
    /** Held back because the SMS gateway was too busy to take them; retried on their own. */
    held_sends?: number;
    /** When the oldest of them was sent. */
    held_since?: string | null;
  };
  /** Messages the networks held back ("BLACKOUT TIME") in the last day. */
  blackout_24h?: number;
  /** Clients whose one-time codes fraud detection paused; each waits on staff. */
  otp_paused: { client_public_id: string; client_name: string; paused_at: string; reason: string | null }[];
  push_configured: boolean;
  error_tracking_configured: boolean;
  checked_at: string;
}

export const messagingHealthKey = ['messaging', 'health'] as const;

export function MessagingHealthPage() {
  const health = useQuery({
    queryKey: messagingHealthKey,
    queryFn: () => api.get<MessagingHealth>('/messaging/health'),
    // A health page left open should not go stale while live updates are quiet.
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Messaging health"
        description="Whether each provider is delivering, from connections, the last day's sends and waiting reports."
        breadcrumbs={[{ label: 'Insight' }, { label: 'Messaging health' }]}
      />

      {health.isLoading ? (
        <LoadingState label="Checking messaging" />
      ) : health.error || !health.data ? (
        <ErrorState
          message={health.error instanceof ApiError ? health.error.message : 'Health could not be loaded.'}
          onRetry={() => void health.refetch()}
        />
      ) : (
        <HealthBody data={health.data} />
      )}
    </div>
  );
}

function HealthBody({ data }: { data: MessagingHealth }) {
  const { worker } = data;
  return (
    <>
      <StatRow>
        <StatCard
          label="Background sends"
          value={worker.enabled ? formatNumber(worker.queued_sends) : 'Off'}
          tone={worker.stalled_sends ? 'danger' : worker.queued_sends ? 'warning' : 'neutral'}
          hint={
            !worker.enabled
              ? 'Every send goes inline; scheduling is unavailable'
              : worker.stalled_sends
                ? `${formatNumber(worker.stalled_sends)} stalled — is the worker running?`
                : 'Queued, waiting for the worker'
          }
        />
        <StatCard
          label="Failing connections"
          value={formatNumber(data.providers.reduce((n, p) => n + p.connections.failing, 0))}
          tone={data.providers.some((p) => p.connections.failing) ? 'danger' : 'success'}
        />
        <StatCard
          label="Push notifications"
          value={data.push_configured ? 'On' : 'Off'}
          tone={data.push_configured ? 'success' : 'neutral'}
          hint={data.push_configured ? undefined : 'Set FCM_SERVICE_ACCOUNT_JSON to turn on'}
        />
        <StatCard
          label="Error tracking"
          value={data.error_tracking_configured ? 'On' : 'Off'}
          tone={data.error_tracking_configured ? 'success' : 'neutral'}
          hint={data.error_tracking_configured ? undefined : 'Set SENTRY_DSN to turn on'}
        />
      </StatRow>

      {worker.held_sends ? (
        <Alert tone="warning" title="The SMS gateway is busy">
          {formatNumber(worker.held_sends)} {worker.held_sends === 1 ? 'send is' : 'sends are'} waiting because the
          gateway is refusing or rate-limiting new messages
          {worker.held_since ? `, the oldest since ${formatRelative(worker.held_since)}` : ''}. Connect tries each again
          on its own, backing off, and never resends a batch the gateway may have taken; clients see it as queued.
          Nothing is needed unless it lasts — then ask the vendor.
        </Alert>
      ) : null}

      <SendReconciliationCard />

      {data.blackout_24h ? (
        <Alert tone="info" title="Networks are holding messages back">
          {formatNumber(data.blackout_24h)} {data.blackout_24h === 1 ? 'message' : 'messages'} came back
          &ldquo;BLACKOUT TIME&rdquo; in the last day: the networks held them back. The report does not say why; clients see
          only that the network did not deliver them.
        </Alert>
      ) : null}

      {data.otp_paused.length ? (
        <Alert tone="danger" title="One-time codes paused by fraud detection">
          <ul className="space-y-1">
            {data.otp_paused.map((row) => (
              <li key={row.client_public_id}>
                <Link
                  to={`/clients/${row.client_public_id}?tab=services`}
                  className="font-medium text-accent hover:underline"
                >
                  {row.client_name}
                </Link>{' '}
                — {row.reason ?? 'codes sent that nobody used'}, {formatRelative(row.paused_at)}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {data.providers.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState title="No messaging connections yet" description="Add a service to a client to see it here." />
          </CardBody>
        </Card>
      ) : (
        data.providers.map((provider) => <ProviderCard key={provider.id} provider={provider} />)
      )}

      <SignInWhatsAppCard />

      <p className="text-2xs text-faint">Checked {formatRelative(data.checked_at)}</p>
    </>
  );
}

function ProviderCard({ provider }: { provider: ProviderHealth }) {
  const { connections } = provider;
  const healthy = connections.failing === 0 && provider.waiting_reports === 0;
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <ChannelIcon channel={provider.channel} className="size-4" />
            {provider.label}
          </span>
        }
        actions={
          healthy ? (
            <Badge tone="success">
              <CheckCircle2 className="size-2.5" />
              Healthy
            </Badge>
          ) : (
            <Badge tone="warning">
              <AlertTriangle className="size-2.5" />
              Needs attention
            </Badge>
          )
        }
      />
      <CardBody className="space-y-4">
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <Figure label="Connections">
            {formatNumber(connections.active)} active
            {connections.paused ? ` · ${formatNumber(connections.paused)} paused` : ''}
          </Figure>
          <Figure label="Verified">
            {formatNumber(connections.verified)} of {formatNumber(connections.total)}
          </Figure>
          <Figure label="Last 24 hours">
            {formatNumber(provider.messages_24h)} messages · {formatNumber(provider.sends_24h)} sends
          </Figure>
          <Figure label="Failure rate">
            {provider.failure_rate === null ? '—' : `${provider.failure_rate}%`}
            {provider.failed_24h ? ` (${formatNumber(provider.failed_24h)})` : ''}
          </Figure>
          <Figure label="Reports overdue">{formatNumber(provider.waiting_reports)}</Figure>
          <Figure label="Low balance">{formatNumber(connections.low_balance)}</Figure>
        </dl>

        {provider.failing.length ? (
          <div className="space-y-2">
            <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-faint">
              <Activity className="size-3.5" />
              Refused by the provider
            </h3>
            <ul className="divide-y divide-line rounded-control border border-line">
              {provider.failing.map((row) => (
                <li key={row.channel_public_id} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2">
                  <Link
                    to={`/clients/${row.client_public_id}?tab=services&service=${row.channel_public_id}`}
                    className="text-sm font-medium text-accent hover:underline"
                  >
                    {row.client_name} · {row.label}
                  </Link>
                  {/* The provider's own words, as written. */}
                  <span className="min-w-0 truncate text-xs text-muted" title={row.last_error}>
                    {row.last_error}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Figure({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs text-faint">{label}</dt>
      <dd className="tabular text-body">{children}</dd>
    </div>
  );
}
